import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Pencil, Plus, Trash2, Eye, Globe, AlertTriangle } from "lucide-react";
import legalContentService, {
  type LegalContent,
  type CreateLegalContentData,
  type UpdateLegalContentData,
} from "@/services/legalContentService";

const LEGAL_CONTENT_TYPES = [
  { value: "terms", label: "Terms and Conditions" },
  { value: "privacy", label: "Privacy Policy" },
  { value: "sales", label: "Sales Conditions" },
  { value: "legal_notices", label: "Legal Notices" },
  { value: "faq", label: "FAQ" },
];

export function LegalContentTab() {
  const { t } = useTranslation();
  const [legalContents, setLegalContents] = useState<LegalContent[]>([]);
  const [loading, setLoading] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedContent, setSelectedContent] = useState<LegalContent | null>(
    null,
  );
  const [contentToDelete, setContentToDelete] = useState<LegalContent | null>(
    null,
  );
  const [formData, setFormData] = useState<CreateLegalContentData>({
    type: "",
    frContent: "",
    enContent: "",
  });

  useEffect(() => {
    fetchLegalContents();
  }, []);

  const fetchLegalContents = async () => {
    try {
      setLoading(true);
      const contents = await legalContentService.getAllLegalContents();
      setLegalContents(contents);
    } catch (error) {
      toast.error("Failed to fetch legal contents");
      console.error("Error fetching legal contents:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      if (!formData.type || !formData.frContent || !formData.enContent) {
        toast.error("Please fill in all required fields for both languages");
        return;
      }

      await legalContentService.createLegalContent(formData);
      toast.success("Legal content created successfully");
      setIsCreateDialogOpen(false);
      resetForm();
      fetchLegalContents();
    } catch (error) {
      toast.error("Failed to create legal content");
      console.error("Error creating legal content:", error);
    }
  };

  const handleUpdate = async () => {
    try {
      if (!selectedContent || !formData.frContent || !formData.enContent) {
        toast.error("Please fill in all required fields for both languages");
        return;
      }

      const updateData: UpdateLegalContentData = {
        frContent: formData.frContent,
        enContent: formData.enContent,
      };

      await legalContentService.updateLegalContent(
        selectedContent.id,
        updateData,
      );
      toast.success("Legal content updated successfully");
      setIsEditDialogOpen(false);
      setSelectedContent(null);
      resetForm();
      fetchLegalContents();
    } catch (error) {
      toast.error("Failed to update legal content");
      console.error("Error updating legal content:", error);
    }
  };

  const handleDeleteClick = (content: LegalContent) => {
    setContentToDelete(content);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!contentToDelete) return;

    try {
      await legalContentService.deleteLegalContent(contentToDelete.id);
      toast.success("Legal content deleted successfully");
      setIsDeleteDialogOpen(false);
      setContentToDelete(null);
      fetchLegalContents();
    } catch (error) {
      toast.error("Failed to delete legal content");
      console.error("Error deleting legal content:", error);
    }
  };

  const handleDeleteCancel = () => {
    setIsDeleteDialogOpen(false);
    setContentToDelete(null);
  };

  const openEditDialog = (content: LegalContent) => {
    setSelectedContent(content);
    setFormData({
      type: content.type,
      frContent: content.frContent,
      enContent: content.enContent,
    });
    setIsEditDialogOpen(true);
  };

  const openViewDialog = (content: LegalContent) => {
    setSelectedContent(content);
    setIsViewDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      type: "",
      frContent: "",
      enContent: "",
    });
    setSelectedContent(null);
  };

  const updateFormData = (
    field: keyof CreateLegalContentData,
    value: string,
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const getTypeLabel = (type: string) => {
    const typeInfo = LEGAL_CONTENT_TYPES.find((t) => t.value === type);
    return typeInfo?.label || type;
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">
            Legal Content Management
          </h2>
          <p className="text-muted-foreground">
            Manage legal content displayed in the footer dialogs (multilingual)
          </p>
        </div>
        <Button
          onClick={() => {
            resetForm();
            setIsCreateDialogOpen(true);
          }}
          className="flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Add Legal Content
        </Button>
      </div>

      <div className="grid gap-4">
        {loading ? (
          <div className="text-center py-8">Loading legal contents...</div>
        ) : legalContents.length === 0 ? (
          <Card>
            <CardContent className="text-center py-8">
              <p className="text-muted-foreground">No legal contents found</p>
            </CardContent>
          </Card>
        ) : (
          legalContents.map((content) => (
            <Card key={content.id}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      {getTypeLabel(content.type)}
                      <Badge
                        variant="outline"
                        className="flex items-center gap-1"
                      >
                        <Globe className="h-3 w-3" />
                        Multilingual
                      </Badge>
                    </CardTitle>
                    <CardDescription>Type: {content.type}</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openViewDialog(content)}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEditDialog(content)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteClick(content)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="text-sm text-muted-foreground">
                    <span className="font-medium">FR:</span>{" "}
                    {content.frContent?.substring(0, 100)}
                    ...
                  </div>
                  <div className="text-sm text-muted-foreground">
                    <span className="font-medium">EN:</span>{" "}
                    {content.enContent?.substring(0, 100)}
                    ...
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Create Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Legal Content (Multilingual)</DialogTitle>
            <DialogDescription>
              Add new legal content that will be displayed in the footer dialogs
              for both languages
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="type">Type *</Label>
              <Select
                value={formData.type}
                onValueChange={(value) => updateFormData("type", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select content type" />
                </SelectTrigger>
                <SelectContent>
                  {LEGAL_CONTENT_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Tabs defaultValue="fr" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="fr">🇫🇷 Français</TabsTrigger>
                <TabsTrigger value="en">🇬🇧 English</TabsTrigger>
              </TabsList>

              <TabsContent value="fr" className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="fr-content">Content (French) *</Label>
                  <Textarea
                    id="fr-content"
                    value={formData.frContent}
                    onChange={(e) =>
                      updateFormData("frContent", e.target.value)
                    }
                    placeholder="Enter content in French..."
                    rows={10}
                    className="min-h-[200px]"
                  />
                </div>
              </TabsContent>

              <TabsContent value="en" className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="en-content">Content (English) *</Label>
                  <Textarea
                    id="en-content"
                    value={formData.enContent}
                    onChange={(e) =>
                      updateFormData("enContent", e.target.value)
                    }
                    placeholder="Enter content in English..."
                    rows={10}
                    className="min-h-[200px]"
                  />
                </div>
              </TabsContent>
            </Tabs>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsCreateDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleCreate}>Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Legal Content (Multilingual)</DialogTitle>
            <DialogDescription>
              Modify the legal content for both languages
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="fr" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="fr">🇫🇷 Français</TabsTrigger>
              <TabsTrigger value="en">🇬🇧 English</TabsTrigger>
            </TabsList>

            <TabsContent value="fr" className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-fr-content">Content (French) *</Label>
                <Textarea
                  id="edit-fr-content"
                  value={formData.frContent}
                  onChange={(e) => updateFormData("frContent", e.target.value)}
                  placeholder="Enter content in French..."
                  rows={10}
                  className="min-h-[200px]"
                />
              </div>
            </TabsContent>

            <TabsContent value="en" className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-en-content">Content (English) *</Label>
                <Textarea
                  id="edit-en-content"
                  value={formData.enContent}
                  onChange={(e) => updateFormData("enContent", e.target.value)}
                  placeholder="Enter content in English..."
                  rows={10}
                  className="min-h-[200px]"
                />
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsEditDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleUpdate}>Update</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedContent ? getTypeLabel(selectedContent.type) : ""}
            </DialogTitle>
            <DialogDescription>Type: {selectedContent?.type}</DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="fr" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="fr">🇫🇷 Français</TabsTrigger>
              <TabsTrigger value="en">🇬🇧 English</TabsTrigger>
            </TabsList>

            <TabsContent value="fr" className="mt-4">
              <div className="prose dark:prose-invert max-w-none">
                <div className="whitespace-pre-wrap text-sm leading-relaxed border rounded-lg p-4 bg-muted">
                  {selectedContent?.frContent}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="en" className="mt-4">
              <div className="prose dark:prose-invert max-w-none">
                <div className="whitespace-pre-wrap text-sm leading-relaxed border rounded-lg p-4 bg-muted">
                  {selectedContent?.enContent}
                </div>
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsViewDialogOpen(false)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Confirm Deletion
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this legal content?
            </DialogDescription>
          </DialogHeader>

          {contentToDelete && (
            <div className="my-4 p-4 border rounded-lg bg-muted">
              <div className="font-medium text-sm">
                {getTypeLabel(contentToDelete.type)}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                Type: {contentToDelete.type}
              </div>
            </div>
          )}

          <div className="text-sm text-muted-foreground">
            <p>
              This action cannot be undone. This will permanently delete the
              legal content for both languages.
            </p>
          </div>

          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={handleDeleteCancel}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              className="flex items-center gap-2"
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
